import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import SocialLinks from '../components/marketing/SocialLinks';

// Iter 260.mk — Marketing website rework. Kewl now sells three services
// under one team: Event Production, Venue Consulting, and Software.
// The homepage is the "three-door" hub; deep product copy lives on the
// /software hub page so the front door doesn't drown newer visitors in
// BEO-speak. All brand tokens preserved from the previous landing.

const HERO_EVENT_IMG = 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?crop=entropy&cs=srgb&fm=webp&q=82&w=1000';
const HERO_EVENT_IMG_SET = 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?crop=entropy&cs=srgb&fm=webp&q=82&w=700 700w, https://images.unsplash.com/photo-1505373877841-8d25f7d46678?crop=entropy&cs=srgb&fm=webp&q=82&w=1000 1000w, https://images.unsplash.com/photo-1505373877841-8d25f7d46678?crop=entropy&cs=srgb&fm=webp&q=82&w=1400 1400w';

// Iter 260.mk.p — Homepage photo refresh. Every image is unique across
// the whole site AND paired to match its slot's content per user.
// WORK_EVENTS — vibrant festival crowd, real Event Operations energy.
const WORK_EVENTS_IMG = 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&q=88&w=1200';
// WORK_CONSULTING — venue speaker at podium, matches Venue Consulting.
const WORK_CONSULTING_IMG = 'https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?crop=entropy&cs=srgb&fm=jpg&q=88&w=1200';
// WORK_SOFTWARE — clean modern laptop workspace (unchanged, unique).
const WORK_SOFTWARE_IMG = 'https://images.unsplash.com/photo-1522542550221-31fd19575a2d?crop=entropy&cs=srgb&fm=jpg&q=88&w=1200';

// PHILOSOPHY — restored to the wine-toast celebration close-up.
const PHILOSOPHY_IMG = 'https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?crop=entropy&cs=srgb&fm=jpg&q=88&w=1200';
// BREAK_ONE — verified candlelit gala tablescape (moved from Event Ops
// CAT_GALA so it lives here at wide format). Warm, elegant, corporate.
const BREAK_ONE_IMG = 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?crop=entropy&cs=srgb&fm=jpg&q=90&w=2200';
// BREAK_TWO — long banquet table with pendant lights & place settings.
const BREAK_TWO_IMG = 'https://images.unsplash.com/photo-1467810563316-b5476525c0f9?crop=entropy&cs=srgb&fm=jpg&q=90&w=2200';
const TESTIMONIAL_BG_IMG = 'https://images.unsplash.com/photo-1511578314322-379afb476865?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800';

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('reveal-up'); observer.unobserve(el); } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
};

const RevealSection = ({ children, className = '', delay = '' }) => {
  const ref = useReveal();
  return <div ref={ref} className={`opacity-0 ${delay} ${className}`}>{children}</div>;
};

const routeCards = [
  {
    eyebrow: 'EVENT OPERATIONS',
    title: 'Your event should feel like one team built it.',
    body: 'Kewl connects the venue, vendors, AV, catering, staffing, logistics, and program into one operating plan, then runs it through final guest departure.',
    cta: 'Explore Event Operations',
    href: '/production',
    img: WORK_EVENTS_IMG,
    testid: 'route-card-events',
  },
  {
    eyebrow: 'VENUE CONSULTING',
    title: 'When the experience is strong, but the operation behind it isn\u2019t.',
    body: 'Kewl helps venues fix unclear ownership, inconsistent processes, broken handoffs, overloaded leaders, staffing gaps, and systems that no longer support the volume of work.',
    cta: 'Explore Venue Consulting',
    href: '/consulting',
    img: WORK_CONSULTING_IMG,
    testid: 'route-card-consulting',
  },
  {
    eyebrow: 'KEWL SOFTWARE',
    title: 'One place to run the work.',
    body: 'Manage events, clients, BEOs, staffing, vendors, portals, payments, documents, and financial visibility inside one connected platform.',
    cta: 'Explore the Platform',
    href: '/software',
    img: WORK_SOFTWARE_IMG,
    testid: 'route-card-software',
  },
];

const testimonials = [
  {
    author: 'Andrea Larson',
    context: '2025 Wedding',
    quote: [
      'Miranda took all the ideas floating around in my head and turned them into a wedding that felt completely like us. She was proactive, organized, calm under pressure, and always one step ahead. I never had to worry about what was happening behind the scenes.',
    ],
  },
];

const LandingPage = () => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const compactViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

  // Warm the API only after the critical first paint. The old immediate
  // request competed with the hero image and fonts on slower mobile links.
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return undefined;
    const warm = () => fetch(`${base}/api/health`, { credentials: 'omit' }).catch(() => {});
    let timer;
    let idleId;
    if (window.requestIdleCallback) idleId = window.requestIdleCallback(warm, { timeout: 3500 });
    else timer = window.setTimeout(warm, 2500);
    return () => {
      if (idleId && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/80 backdrop-blur-xl border-b border-[#EDE4D6]/40" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-heading text-2xl text-[#4A3C31] tracking-tight">KEWL</span>
              <span className="text-[#A89685] text-xs font-medium tracking-wider uppercase hidden sm:block">Event Solutions</span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link to="/production" className="text-[#5C554E] hover:text-[#4A3C31] transition-colors text-sm font-medium" data-testid="nav-events-link">Event Operations</Link>
              <Link to="/consulting" className="text-[#5C554E] hover:text-[#4A3C31] transition-colors text-sm font-medium" data-testid="nav-consulting-link">Venue Consulting</Link>
              <Link to="/software" className="text-[#5C554E] hover:text-[#4A3C31] transition-colors text-sm font-medium" data-testid="nav-software-link">Software</Link>
              <Link to="/about" className="text-[#5C554E] hover:text-[#4A3C31] transition-colors text-sm font-medium" data-testid="nav-about-link">About</Link>
              <Link to="/login">
                <span className="text-[#4A3C31] text-sm font-medium hover:text-[#5C554E] transition-colors" data-testid="login-btn">Sign In</span>
              </Link>
              <Link to="/contact">
                <Button className="rounded-full bg-[#4A3C31] hover:bg-[#5C554E] text-[#FDFBF7] px-6 py-2.5 text-sm font-medium" data-testid="nav-book-call-btn">
                  Work with Us
                </Button>
              </Link>
            </div>

            <button className="md:hidden p-2 text-[#4A3C31]" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
              {menuOpen ? <X strokeWidth={1.5} className="w-5 h-5" /> : <Menu strokeWidth={1.5} className="w-5 h-5" />}
            </button>
          </div>

          {menuOpen && (
            <div className="md:hidden pb-6 space-y-3 border-t border-[#EDE4D6]/40 pt-4 bg-[#FDFBF7] -mx-6 px-6" data-testid="mobile-menu-drawer">
              <Link to="/production" className="block text-[#5C554E] py-2 text-sm" onClick={() => setMenuOpen(false)}>Event Operations</Link>
              <Link to="/consulting" className="block text-[#5C554E] py-2 text-sm" onClick={() => setMenuOpen(false)}>Venue Consulting</Link>
              <Link to="/software" className="block text-[#5C554E] py-2 text-sm" onClick={() => setMenuOpen(false)}>Software</Link>
              <Link to="/about" className="block text-[#5C554E] py-2 text-sm" onClick={() => setMenuOpen(false)}>About</Link>
              <Link to="/login" className="block text-[#4A3C31] font-medium py-2 text-sm" onClick={() => setMenuOpen(false)}>Sign In</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)}>
                <Button className="w-full rounded-full bg-[#4A3C31] hover:bg-[#5C554E] text-[#FDFBF7] text-sm">Work with Us</Button>
              </Link>
            </div>
          )}
        </div>
      </nav>

      <main id="main">
      {/* Hero — the primary sales headline (rewritten per user):
          three-line "we run / we fix / we built" makes the business model
          instantly readable. Brand promise ("we notice the detail…") is
          moved lower into the Kewl Standard section so it lands only
          after the visitor already knows what Kewl does. */}
      <section className="pt-28 md:pt-36 pb-20 md:pb-28 px-6 md:px-12" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#A89685] mb-7" data-testid="hero-eyebrow">
                Event Operations <span className="text-[#DDD3C3] mx-1.5">&middot;</span> Venue Consulting <span className="text-[#DDD3C3] mx-1.5">&middot;</span> Software
              </p>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-[5.25rem] text-[#4A3C31] leading-[0.98] tracking-tight mb-8" data-testid="hero-headline">
                We run the room.<br />
                We fix the operation.<br />
                <em className="font-light italic text-[#A89685]">We built the system for both.</em>
              </h1>
              <p className="text-lg lg:text-xl text-[#5C554E] leading-relaxed mb-10 max-w-2xl">
                Kewl operates and produces complex events, strengthens the operations behind venues, and provides the software that keeps every detail, person, document, and dollar connected.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <Link to="/contact">
                  <Button
                    className="rounded-full bg-[#4A3C31] hover:bg-[#5C554E] text-[#FDFBF7] px-8 py-6 text-sm font-medium transition-all"
                    data-testid="hero-primary-cta"
                  >
                    Tell Us What You&rsquo;re Working On
                    <ArrowRight strokeWidth={1.5} className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/software">
                  <Button
                    variant="outline"
                    className="rounded-full border-[#4A3C31] text-[#4A3C31] hover:bg-[#4A3C31] hover:text-[#FDFBF7] px-8 py-6 text-sm font-medium transition-all"
                    data-testid="hero-secondary-cta"
                  >
                    Explore the Software
                  </Button>
                </Link>
              </div>
              <p className="text-[13px] text-[#8A6D3F] italic tracking-tight" data-testid="hero-availability">
                Founder-led. Operator-built. Available for select projects nationwide.
              </p>
            </div>

            {/* Right column — real event photo with the real Kewl
                software screenshot overlapping bottom-left (per user:
                keep the overlap composition, just make it bigger). */}
            <div className="lg:col-span-5 relative pb-24 md:pb-32">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-[#EDE4D6] shadow-[0_24px_80px_rgba(74,60,49,0.18)]">
                <img
                  src={HERO_EVENT_IMG}
                  srcSet={HERO_EVENT_IMG_SET}
                  sizes="(min-width: 1024px) 42vw, 100vw"
                  alt="A live conference in a large venue &mdash; stage lit, screens on, audience in place, production truss overhead"
                  className="w-full h-full object-cover"
                  loading={compactViewport ? 'lazy' : 'eager'}
                  fetchPriority={compactViewport ? 'low' : 'high'}
                  decoding="async"
                />
              </div>
              {/* Software screenshot — larger (~90% width), less rotation,
                  extends below the photo so it reads clearly. */}
              <div className="hidden md:block absolute -bottom-4 -left-12 lg:-left-24 w-[92%] rounded-xl overflow-hidden bg-white shadow-[0_30px_60px_rgba(74,60,49,0.28)] border border-[#EDE4D6] -rotate-[1.5deg]">
                <img
                  src="/software-beo.webp"
                  alt="A real Kewl event workspace &mdash; the Event P&L view for a live production"
                  className="w-full h-auto block"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              {/* Mobile fallback — stacked below, no rotation */}
              <div className="md:hidden mt-4 rounded-xl overflow-hidden bg-white shadow-[0_16px_40px_rgba(74,60,49,0.18)] border border-[#EDE4D6]">
                <img
                  src="/software-beo.webp"
                  alt="A real Kewl event workspace &mdash; the Event P&L view"
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proof strip — replaces the "3 services / one partner" floating
          badge with specific operational categories + three specific,
          non-fabricated proof statements (per user). Placed immediately
          under hero for trust-first flow. */}
      <section className="py-14 md:py-20 px-6 md:px-12 bg-[#F9F6F0] border-y border-[#EDE4D6]/60" data-testid="proof-strip">
        <div className="max-w-7xl mx-auto">
          <RevealSection>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#A89685] mb-4 text-center">Built From Real Operations</p>
            <p className="text-center text-[#4A3C31] text-lg md:text-xl leading-relaxed mb-12 max-w-3xl mx-auto" data-testid="proof-categories">
              Venues <span className="text-[#DDD3C3] mx-2">&middot;</span>
              Corporate Programs <span className="text-[#DDD3C3] mx-2">&middot;</span>
              Conferences <span className="text-[#DDD3C3] mx-2">&middot;</span>
              Concerts <span className="text-[#DDD3C3] mx-2">&middot;</span>
              Festivals <span className="text-[#DDD3C3] mx-2">&middot;</span>
              Galas <span className="text-[#DDD3C3] mx-2">&middot;</span>
              Private Events
            </p>
            <div className="grid md:grid-cols-3 gap-8 md:gap-12 max-w-5xl mx-auto">
              <div data-testid="proof-point-1">
                <p className="font-heading text-xl text-[#4A3C31] mb-2 leading-tight">One operating partner</p>
                <p className="text-[15px] text-[#5C554E] leading-relaxed">From the first plan through final execution.</p>
              </div>
              <div data-testid="proof-point-2">
                <p className="font-heading text-xl text-[#4A3C31] mb-2 leading-tight">Real field experience</p>
                <p className="text-[15px] text-[#5C554E] leading-relaxed">Built inside venues, production teams, agencies, and live events.</p>
              </div>
              <div data-testid="proof-point-3">
                <p className="font-heading text-xl text-[#4A3C31] mb-2 leading-tight">Systems that stay</p>
                <p className="text-[15px] text-[#5C554E] leading-relaxed">Processes, tools, and software your team can continue using.</p>
              </div>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* Photo break 1 */}
      <section className="relative" data-testid="landing-break-1">
        <div className="h-[50vh] md:h-[60vh] w-full overflow-hidden bg-[#EDE4D6]">
          <img src={BREAK_ONE_IMG} alt="A room set for a large-scale gathering" className="w-full h-full object-cover" loading="lazy" decoding="async" />
        </div>
      </section>

      {/* Three Ways To Work — updated per user: new eyebrow, headline,
          body. Removed "you'll usually find us in one of three rooms" — 
          it implied Kewl had three separate departments. */}
      <section id="routes" className="py-24 md:py-32 px-6 md:px-12 bg-[#F9F6F0]" data-testid="routes-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-14 md:mb-20">
            <RevealSection>
              <p className="overline mb-5">Three Ways To Work With Us</p>
              <h2 className="font-heading text-4xl sm:text-5xl text-[#4A3C31] leading-tight mb-6">
                Start with what needs<br />
                <em className="font-light italic text-[#A89685]">to work better.</em>
              </h2>
              <p className="text-lg text-[#5C554E] leading-relaxed max-w-xl">
                Some clients need the room operated. Some need the venue behind it strengthened. Others need one system to run the entire operation. Kewl was built to handle all three.
              </p>
            </RevealSection>
          </div>

          <div className="grid md:grid-cols-3 gap-5 md:gap-6">
            {routeCards.map((card, i) => (
              <RevealSection key={card.href} delay={`delay-${(i + 1) * 100}`}>
                <Link to={card.href} className="block h-full group" data-testid={card.testid}>
                  <div className="h-full flex flex-col rounded-2xl overflow-hidden bg-white border border-[#EDE4D6]/60 shadow-[0_4px_24px_rgba(74,60,49,0.03)] hover:shadow-[0_12px_36px_rgba(74,60,49,0.08)] hover:border-[#C4A676]/40 transition-all">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={card.img} alt={card.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async" />
                    </div>
                    <div className="p-7 md:p-8 flex flex-col flex-1">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#A89685] mb-3">{card.eyebrow}</p>
                      <h3 className="font-heading text-2xl text-[#4A3C31] mb-4 leading-tight">{card.title}</h3>
                      <p className="text-[#5C554E] leading-relaxed text-[15px] mb-6 flex-1">{card.body}</p>
                      <span className="inline-flex items-center gap-1.5 text-[#4A3C31] text-sm font-medium group-hover:gap-2.5 transition-all">
                        {card.cta}
                        <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>


      {/* Concise "we don't advise from the sidelines" — replaces the
          longer Scope + Connective Tissue sections per user. All the
          Build/Join/Run detail moved to the Event Operations page. */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#4A3C31] text-[#FDFBF7]" data-testid="connective-tissue">
        <div className="max-w-4xl mx-auto text-center">
          <RevealSection>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#C4A676] mb-6">Why These Three Belong Together</p>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#FDFBF7] leading-[1.05] mb-8" data-testid="connective-headline">
              We don&rsquo;t advise<br />
              <em className="font-light italic text-[#DDD3C3]">from the sidelines.</em>
            </h2>
            <p className="text-lg md:text-xl text-[#DDD3C3] leading-relaxed max-w-2xl mx-auto">
              We operate events, improve the venues behind them, and build those improvements into the platform.
            </p>
          </RevealSection>
        </div>
      </section>

      {/* Photo break 2 */}
      <section className="relative" data-testid="landing-break-2">
        <div className="h-[45vh] md:h-[60vh] w-full overflow-hidden bg-[#EDE4D6]">
          <img src={BREAK_TWO_IMG} alt="An audience gathered under stage lighting" className="w-full h-full object-cover" loading="lazy" decoding="async" />
        </div>
      </section>

      {/* What Clients Say — testimonials. Currently one; array supports
          adding more over time without a layout rewrite. */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto">
          <RevealSection>
            <div className="text-center mb-14 md:mb-20">
              <p className="overline mb-5">What Clients Say</p>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#4A3C31] leading-[1.05]">
                Trusted with{' '}
                <em className="font-light italic text-[#A89685]">the details that matter.</em>
              </h2>
            </div>
          </RevealSection>
          <div className="space-y-14 md:space-y-16">
            {testimonials.map((t) => (
              <RevealSection key={t.author}>
                <figure className="relative pl-6 md:pl-12 border-l-2 border-[#C4A676]" data-testid={`testimonial-${t.author.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <span aria-hidden className="absolute -top-6 left-4 md:left-10 font-heading text-8xl md:text-9xl text-[#C4A676]/25 leading-none select-none">&ldquo;</span>
                  <blockquote className="relative space-y-5 font-heading font-light italic text-[#4A3C31] text-xl md:text-2xl lg:text-[1.55rem] leading-[1.5]">
                    {t.quote.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </blockquote>
                  <figcaption className="mt-8 flex items-center gap-4">
                    <span className="block h-px w-10 bg-[#C4A676]" />
                    <div>
                      <p className="text-[#4A3C31] font-medium not-italic">{t.author}</p>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#A89685] mt-1">{t.context}</p>
                    </div>
                  </figcaption>
                </figure>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* Founder-Led — homepage version (privacy-safe per user).
          Employment history & venue names moved to About page. */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#F9F6F0]" data-testid="founder-preview">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-[#EDE4D6] shadow-[0_20px_60px_rgba(74,60,49,0.15)]">
              <img
                src="/founder.webp"
                alt="The founder of Kewl Event Solutions"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <RevealSection>
              <p className="overline mb-5">Founder-Led</p>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#4A3C31] leading-[1.05] mb-8">
                Built by an operator<br />
                <em className="font-light italic text-[#A89685]">who has been responsible for the outcome.</em>
              </h2>
              <div className="space-y-5 text-lg text-[#5C554E] leading-relaxed">
                <p>
                  Kewl was founded by an operator who spent years producing events, operating venues, leading teams, managing vendors, and solving the problems guests were never supposed to see.
                </p>
                <p>
                  The best operators do more than react quickly. They build systems that prevent the problem in the first place.
                </p>
              </div>
              <div className="mt-10">
                <Link to="/about">
                  <Button variant="outline" className="rounded-full border-[#4A3C31] text-[#4A3C31] hover:bg-[#4A3C31] hover:text-[#FDFBF7] px-6 py-4 text-sm font-medium" data-testid="founder-preview-cta">
                    Meet Miranda
                    <ArrowRight strokeWidth={1.5} className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </RevealSection>
          </div>
        </div>
      </section>

      {/* The Kewl Standard — 4 pillars (per user): a house style, not a
          template. Dark editorial section with 4 principle cards. */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 bg-[#4A3C31] overflow-hidden" data-testid="quote-section">
        <div className="absolute inset-0 opacity-15">
          <img src={TESTIMONIAL_BG_IMG} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#4A3C31]/60 via-[#4A3C31]/75 to-[#4A3C31]/90" />
        <div className="relative max-w-6xl mx-auto">
          <div className="max-w-3xl mb-14 md:mb-20">
            <RevealSection>
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C4A676] mb-6">The Kewl Standard</p>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#FDFBF7] leading-[1.05] mb-4">
                A house style,<br />
                <em className="font-light italic text-[#DDD3C3]">not a template.</em>
              </h2>
            </RevealSection>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-10">
            {[
              {
                pillar: 'HANDS-ON',
                title: 'Direct involvement from the first conversation through final execution.',
                testid: 'kewl-pillar-hands-on',
              },
              {
                pillar: 'AHEAD',
                title: 'Issues are handled before they ever reach the room.',
                testid: 'kewl-pillar-ahead',
              },
              {
                pillar: 'STEADY',
                title: 'Clear decisions and calm leadership when plans change.',
                testid: 'kewl-pillar-steady',
              },
            ].map((p, i) => (
              <RevealSection key={p.pillar} delay={`delay-${(i + 1) * 100}`}>
                <div className="border-t border-[#C4A676]/40 pt-6" data-testid={p.testid}>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#C4A676] mb-4">{p.pillar}</p>
                  <h3 className="font-heading text-xl md:text-2xl text-[#FDFBF7] leading-[1.2]">{p.title}</h3>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA — rewritten per user: replaces "have an event / ops
          problem to solve?" framing with a lead-agnostic prompt that
          removes the burden of picking the right service upfront. */}
      <section className="py-24 md:py-36 px-6 md:px-12 bg-[#F9F6F0]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <RevealSection>
            <p className="overline mb-5">Get in Touch</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-[#4A3C31] leading-tight mb-6">
              What are you trying<br />
              <em className="font-light italic text-[#A89685]">to make work better?</em>
            </h2>
            <p className="text-lg text-[#5C554E] leading-relaxed mb-10 max-w-2xl mx-auto">
              Tell us about the event, venue operation, or system your team has outgrown. You don&rsquo;t need to know which Kewl service you need. We&rsquo;ll help identify the right place to start.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  className="rounded-full bg-[#4A3C31] hover:bg-[#5C554E] text-[#FDFBF7] px-10 py-6 text-sm font-medium transition-all"
                  data-testid="cta-contact-btn"
                >
                  Start a Conversation
                  <ArrowRight strokeWidth={1.5} className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <p className="mt-8 text-[13px] text-[#8A6D3F] italic tracking-tight" data-testid="cta-reply-window">
              You&rsquo;ll hear directly from Kewl within one business day.
            </p>
          </RevealSection>
        </div>
      </section>

      </main>

      {/* Footer */}
      <footer className="py-14 md:py-20 px-6 md:px-12 border-t border-[#EDE4D6] bg-[#FDFBF7]" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-8 mb-12">
            <div>
              <p className="font-heading text-xl text-[#4A3C31] mb-1">KEWL</p>
              <p className="text-[#A89685] text-[10px] tracking-[0.2em] uppercase mb-4">Event Solutions</p>
              <p className="text-[#5C554E] text-sm leading-relaxed max-w-[220px]">
                Event Operations. Venue Consulting. Software. One team behind all three.
              </p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#A89685] mb-4">Services</p>
              <ul className="space-y-2 text-sm">
                <li><Link to="/production" className="text-[#5C554E] hover:text-[#4A3C31] transition-colors">Event Operations</Link></li>
                <li><Link to="/consulting" className="text-[#5C554E] hover:text-[#4A3C31] transition-colors">Venue Consulting</Link></li>
                <li><Link to="/software" className="text-[#5C554E] hover:text-[#4A3C31] transition-colors">Software</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#A89685] mb-4">Company</p>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-[#5C554E] hover:text-[#4A3C31] transition-colors">About</Link></li>
                <li><Link to="/#work" className="text-[#5C554E] hover:text-[#4A3C31] transition-colors">Recent Work</Link></li>
                <li><Link to="/contact" className="text-[#5C554E] hover:text-[#4A3C31] transition-colors">Work With Us</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#A89685] mb-4">Legal</p>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms" className="text-[#5C554E] hover:text-[#4A3C31] transition-colors" data-testid="footer-terms-link">Terms</Link></li>
                <li><Link to="/privacy" className="text-[#5C554E] hover:text-[#4A3C31] transition-colors" data-testid="footer-privacy-link">Privacy</Link></li>
                <li><Link to="/login" className="text-[#5C554E] hover:text-[#4A3C31] transition-colors">Sign In</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-[#EDE4D6]/60 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-[#A89685]" data-testid="landing-footer-copyright">
              &copy; {new Date().getFullYear()} Kewl Event Solutions
              <span className="mx-2 text-[#DDD3C3]">·</span>
              Phoenix, AZ · traveling for the right work
              <span className="mx-2 text-[#DDD3C3]">·</span>
              <span
                className="font-semibold uppercase tracking-wider text-[#4A3C31]"
                data-testid="landing-patent-pending"
              >
                Patent Pending
              </span>
            </p>
            <SocialLinks />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
